/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/jsx-closing-tag-location */
import React from 'react';
import { PageHeader, Descriptions, Timeline } from 'antd';
import { EnvironmentTwoTone } from '@ant-design/icons';
import { DELIVERY_OPERATIONS } from '../../types/Delivery';
import { JobDetailedDelivery } from '../../types/Job';

interface JobDeliverTimelinePropsInterface {
    deliveries: JobDetailedDelivery[];
}

const JobDeliveryTimeline: React.FC<JobDeliverTimelinePropsInterface> = (
    props: JobDeliverTimelinePropsInterface,
): React.ReactElement => {
    const { deliveries } = props;

    const deliveryItem = (delivery: any, operationType: string): JSX.Element => {
        /* eslint-disable prettier/prettier */
        return (
            <Descriptions
                title={<span className="text-capitalize">{operationType}</span>}
                layout="vertical"
                size="small"
            >
                <Descriptions.Item
                    label={
                        <p className="m-0 d-flex align-items-baseline">
                            <EnvironmentTwoTone className="mr-2" />
                            <span className="font-weight-bold">Coordinates</span>
                        </p>
                    }
                >
                    <p className="m-0 w-75">
                        <span className="w-50 d-inline-block">Latitude: </span>
                        <span className="w-50 d-inline-block text-secondary text-right" data-testid="latitude">
                            {delivery[operationType].latitude || '-'}
                        </span>
                        <br />
                        <span className="w-50 d-inline-block">Longitude: </span>
                        <span className="w-50 d-inline-block text-secondary text-right" data-testid="longitude">
                            {delivery[operationType].longitude || '-'}
                        </span>
                        <br />
                        <span className="w-50 d-inline-block">Provided by client: </span>
                        <span className="w-50 d-inline-block text-secondary text-right" data-testid="providedByClient">
                            {delivery[operationType].client_side_provided_coordinates ? 'Yes' : 'No'}
                        </span>
                        <a
                            href={`http://www.google.com/maps/place/${delivery[operationType].latitude},${delivery[operationType].longitude}`}
                            title="Show on Map"
                            className="d-block w-100"
                            target="blank"
                            data-testid="google-maps-coordinate-link"
                        >
                            Show on Map
                        </a>
                    </p>
                </Descriptions.Item>
                <Descriptions.Item label={<strong>Client info.</strong>}>
                    <p className="m-0">
                        <span>Company: </span>
                        <span className="text-secondary" data-testid="company">
                            {delivery[operationType].contact.company || '-'}
                        </span>
                        <br />
                        <span>Full name: </span>
                        <span className="text-secondary" data-testid="contactFullname">{`${
                            delivery[operationType].contact.firstname || '-'
                        } ${delivery[operationType].contact.lastname || '-'}`}</span>
                        <br />
                        <span>E-mail: </span>
                        <a
                            href={`mailto:${delivery[operationType].contact.email}`}
                            title="E-mail"
                            target="blank"
                            className="text-lowercase"
                            data-testid="email"
                        >
                            {delivery[operationType].contact.email || '-'}
                        </a>
                        <br />
                        <span>Phone nº: </span>
                        <span className="text-secondary" data-testid="phone">
                            {delivery[operationType].contact.phone || '-'}
                        </span>
                    </p>
                </Descriptions.Item>
                <Descriptions.Item label={<strong>Address</strong>}>
                    <p className="m-0 text-secondary">
                        <span className="d-block w-100" data-testid="originalAddress">
                            {delivery[operationType].address.original_address || '-'}
                        </span>
                        <a
                            href={`http://www.google.com/maps/place/${delivery[operationType].address.original_address}`}
                            title="Show on Map"
                            className="d-block w-100"
                            target="blank"
                            data-testid="google-maps-address-link"
                        >
                            Show on Map
                        </a>
                    </p>
                </Descriptions.Item>
                <Descriptions.Item label={<strong>Package description</strong>}>
                    <p className="m-0 text-secondary" data-testid="packageDescription">
                        {delivery.packageDescription || '-'}
                    </p>
                </Descriptions.Item>
                <Descriptions.Item label={<strong>Comment</strong>}>
                    <p className="m-0 text-secondary" data-testid="comment">
                        {delivery[operationType].comment || '-'}
                    </p>
                </Descriptions.Item>
                {operationType === DELIVERY_OPERATIONS.DROP_OFF && (
                    <Descriptions.Item label={<strong>Load</strong>}>
                        <p className="m-0 text-secondary" data-testid="load">
                            {delivery[operationType].load || '-'}
                        </p>
                    </Descriptions.Item>
                )}
            </Descriptions>
        );
        /* eslint-enable prettier/prettier */
    };

    return (
        <div className="shadow-sm">
            <PageHeader
                className="site-page-header py-2 justify-content-end"
                title={<p className="h5 m-0">Deliveries</p>}
                extra={[
                    <span key="num_deliveries" className="text-secondary" data-testid="num_deliveries">
                        {`Nº deliveries: ${deliveries.length}`}
                    </span>,
                ]}
            />
            {deliveries.map((delivery) => (
                <div key={delivery.key} className="px-4 py-2" data-testid={`delivery-id-${delivery.deliveryId}`}>
                    <PageHeader
                        title={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <p className="m-0" style={{ fontSize: '14px' }}>
                                <span className="text-primary" style={{ fontSize: '15px' }}>
                                    <strong>Middleware Delivery Id: </strong>
                                </span>
                                <span className="text-secondary" style={{ fontWeight: 'normal' }}>
                                    {delivery.deliveryId}
                                </span>
                                <br />
                                <span>
                                    <strong>Client reference: </strong>
                                </span>
                                <span className="text-secondary text-uppercase" style={{ fontWeight: 'normal' }}>
                                    {delivery.clientReference}
                                </span>
                            </p>
                        }
                        extra={[
                            <p className="m-0" style={{ fontSize: '14px' }} key={1}>
                                <span>
                                    <strong>Package type: </strong>
                                </span>
                                <span className="text-capitalize">{delivery.packageType}</span>
                            </p>,
                        ]}
                        className="site-page-header p-0"
                    />
                    <Timeline className="py-2">
                        <Timeline.Item color="#ff9c6e" data-testid={`pu-${delivery.pickup.id}`}>
                            {deliveryItem(delivery, DELIVERY_OPERATIONS.PICK_UP)}
                        </Timeline.Item>
                        <Timeline.Item color="#4b8b26" data-testid={`do-${delivery.dropoff.id}`}>
                            {deliveryItem(delivery, DELIVERY_OPERATIONS.DROP_OFF)}
                        </Timeline.Item>
                    </Timeline>
                    {/* eslint-enable prettier/prettier */}
                </div>
            ))}
        </div>
    );
};

export default JobDeliveryTimeline;
